import { useCallback, useMemo, useRef, useState } from "react";
import { ColDef, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import {
	aggridHeaderClass,
	commonNumberCellStyle,
} from "src/components/common/common";
import Grid_Select from "src/components/common/gridWrapper/Grid_Select";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import {
	defaultColDef,
	getDefaultRowIndexColumnConfig,
} from "src/utils/grid/gridStyleUtils";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { formatNumber } from "src/utils/number/numberUtils";
import { formatDate } from "src/utils/string/stringUtil";

import {
	defaultTQC2011_R01HeaderDto,
	TQC2011_R01HeaderDto,
} from "./Dto_TQC2011_R01";
import { useTQC2011_R01Store } from "./Store_TQC2011_R01";

const COLUMN_STATE_KEY = "TQC2011_R01-header-column-state";

interface Props {
	onHeaderRowClick: (row: TQC2011_R01HeaderDto) => void;
}

export const HeaderTable_TQC2011_R01 = ({ onHeaderRowClick }: Props) => {
	const {
		TQC2011_R01List,
		setTQC2011_R01List,
		selectedTQC2011_R01,
		initSelectedTQC2011_R01,
		setColumnState,
		scrollTop,
		setScrollTop,
	} = useTQC2011_R01Store();
	const gridRef = useRef<AgGridReact<TQC2011_R01HeaderDto>>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null);
	const [, setHoveredRowIndex] = useState<number | null>(null);

	const { getRowId, createRowClassRules } =
		useGridSettingContent<TQC2011_R01HeaderDto>({
			gridRef,
			key: "valueIndex",
			gridData: TQC2011_R01List,
			defaultGridData: defaultTQC2011_R01HeaderDto,
			selectedRow: selectedTQC2011_R01,
			originalGridData: TQC2011_R01List,
			updateRow: () => undefined,
		});

	const numberColumn = useCallback(
		(field: keyof TQC2011_R01HeaderDto, headerName: string, width = 90) => ({
			headerName,
			field,
			width,
			headerClass: aggridHeaderClass,
			cellStyle: commonNumberCellStyle,
			valueFormatter: ({ value }: { value: number | null | undefined }) =>
				value == null ? "" : formatNumber(value),
		}),
		[],
	);

	const columnDefs: ColDef<TQC2011_R01HeaderDto>[] = useMemo(
		() => [
			...getDefaultRowIndexColumnConfig(),
			{ headerName: "사업장", field: "compNm", width: 100, headerClass: aggridHeaderClass },
			{
				headerName: "생산일자",
				field: "productDt",
				width: 105,
				headerClass: aggridHeaderClass,
				valueFormatter: ({ value }) => (value ? formatDate(new Date(value)) : ""),
			},
			{ headerName: "생산실적No", field: "productResultNo", width: 135, headerClass: aggridHeaderClass },
			{ headerName: "생산지시No", field: "productOrderNo", width: 135, headerClass: aggridHeaderClass },
			{ headerName: "작업장", field: "workplaceNm", width: 110, headerClass: aggridHeaderClass },
			{ headerName: "작업조", field: "jobTeamNm", width: 90, headerClass: aggridHeaderClass },
			{ headerName: "작업자", field: "productEmpNm", width: 90, headerClass: aggridHeaderClass },
			{ headerName: "품목코드", field: "itemCd", width: 120, headerClass: aggridHeaderClass },
			{ headerName: "품명", field: "itemNm", width: 140, headerClass: aggridHeaderClass },
			{ headerName: "규격", field: "itemSpec", width: 120, headerClass: aggridHeaderClass },
			numberColumn("productQty", "생산수량"),
			numberColumn("badQty", "불량수량"),
			numberColumn("acceptQty", "양품수량"),
			{ headerName: "단위", field: "itemUnit", width: 65, headerClass: aggridHeaderClass },
			{
				headerName: "검사여부",
				colId: "inspectStatus",
				width: 90,
				headerClass: aggridHeaderClass,
				valueGetter: ({ data, node }) =>
					node?.rowPinned ? "" : Number(data?.inspectionCount ?? 0) > 0 ? "검사완료" : "미검사",
			},
		],
		[numberColumn],
	);

	const pinnedBottomRow = useMemo<TQC2011_R01HeaderDto[]>(() => [{
		...defaultTQC2011_R01HeaderDto,
		compNm: `${TQC2011_R01List.length}건`,
		productQty: TQC2011_R01List.reduce((sum, row) => sum + Number(row.productQty || 0), 0),
		badQty: TQC2011_R01List.reduce((sum, row) => sum + Number(row.badQty || 0), 0),
		acceptQty: TQC2011_R01List.reduce((sum, row) => sum + Number(row.acceptQty || 0), 0),
	}], [TQC2011_R01List]);

	const rowClassRules = useMemo(
		() => createRowClassRules<TQC2011_R01HeaderDto>({
			idKey: "valueIndex",
			selectedList: selectedTQC2011_R01?.valueIndex
				? [selectedTQC2011_R01.valueIndex]
				: [],
			deletedList: [],
		}),
		[createRowClassRules, selectedTQC2011_R01],
	);

	const handleRowClick = useCallback(
		(event: RowClickedEvent<TQC2011_R01HeaderDto>) => {
			if (!event.node.rowPinned && event.data) onHeaderRowClick(event.data);
		},
		[onHeaderRowClick],
	);

	return (
		<GridContextWrapper<TQC2011_R01HeaderDto>
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={TQC2011_R01List}
			setPaddedGridData={setTQC2011_R01List}
			initSelected={initSelectedTQC2011_R01}
			defaultRowData={defaultTQC2011_R01HeaderDto}
			allSelected={() => undefined}
			baseColumnDefs={columnDefs}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
		>
			<Grid_Select<TQC2011_R01HeaderDto>
				ref={gridRef}
				rowData={TQC2011_R01List}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={rowClassRules}
				onRowClicked={handleRowClick}
				pinnedBottomRowData={pinnedBottomRow}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				setHoveredRowIndex={setHoveredRowIndex}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
